import { useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Button,
  Center,
  ELEVATION,
  SIZE,
  Spinner,
  Stack,
  Tabs,
  Text,
  Badge,
} from '@quinyx/ui';

import { withCustomerLayout } from '../../../layouts/customer';
import CustomerForm from '../../../forms/customer';
import {
  ICustomerBasicInfo,
  ICustomerUpdatePayload,
  IZoined,
} from '../../../api/interfaces/customer';
import LicenseForm from '../../../forms/license';
import {
  useCreateZoined,
  useGetCustomer,
  useGetZoined,
  useUpdateCustomer,
  useUpdateZoined,
} from '../../../hooks/customer';
import Modules from '../../../forms/modules';
import Config from '../../../forms/config';
import ZoinedForm from '../../../forms/zoined';
import Actions from '../../../forms/actions';
import { addRecentlyVisitedCustomer } from '../../../utils/recentlyVisitedCustomers';
import { CONFIG } from '@/api/config';
import { useUser } from '@/context/authenticatedUser';
import { useTranslate } from '@/hooks/translate';

const TAB_IDS = {
  BASIC_INFO: 'basic',
  LICENSE: 'license',
  MODULES: 'modules',
  CONFIGS: 'configs',
  ZOINED: 'zoined',
  ACTIONS: 'actions',
} as const;

type TabId = (typeof TAB_IDS)[keyof typeof TAB_IDS];

const TAB_ORDER: TabId[] = [
  TAB_IDS.BASIC_INFO,
  TAB_IDS.LICENSE,
  TAB_IDS.MODULES,
  TAB_IDS.CONFIGS,
  TAB_IDS.ZOINED,
  TAB_IDS.ACTIONS,
];

const DEFAULT_TAB = TAB_IDS.BASIC_INFO;

const Customer = () => {
  const { t } = useTranslate();
  const { customerId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: initialZoined } = useGetZoined(String(customerId));
  const { createZoined } = useCreateZoined();
  const { updateZoined } = useUpdateZoined();
  const { data: customer, isLoading } = useGetCustomer(String(customerId));
  const { updateCustomer, error: updateError } = useUpdateCustomer();
  const navigate = useNavigate();
  const user = useUser();
  const isMarkedForDeletion = Boolean(customer?.deleteBy);

  const activeTabId = useMemo(() => {
    const tabFromUrl = searchParams.get('tab');
    return tabFromUrl && TAB_ORDER.includes(tabFromUrl as TabId)
      ? (tabFromUrl as TabId)
      : DEFAULT_TAB;
  }, [searchParams]);

  const activeTabIndex = useMemo(
    () => TAB_ORDER.indexOf(activeTabId),
    [activeTabId],
  );

  const handleTabChange = (tabId: TabId) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set('tab', tabId);
      return newParams;
    });
  };

  useEffect(() => {
    if (customer?.name && customerId) {
      addRecentlyVisitedCustomer(user.id, String(customerId), customer.name);
    }
  }, [customer?.name, customerId, user?.id]);

  const handleSubmit = (customerData: ICustomerBasicInfo) => {
    if (!customer) {
      return;
    }

    const data: ICustomerUpdatePayload = {
      ...customerData,
      customerId: customer.customerId,
      rootStoreId: customer.rootStoreId,
      email: customer.email,
      companyAlias: customerData.companyAlias || undefined,
    };
    updateCustomer([String(customerId), data]);
  };

  const handleSubmitZoined = (zoined: IZoined) => {
    if (initialZoined) {
      updateZoined([String(customerId), zoined]);
    } else {
      createZoined([String(customerId), zoined]);
    }
  };

  if (isLoading) {
    return (
      <Center>
        <Spinner size={SIZE.L} />
      </Center>
    );
  }

  return (
    <Stack spacing={SIZE.M}>
      <Stack
        direction={['column', 'column', 'row']}
        alignItems={['flex-start', 'flex-start', 'center']}
        spacing={SIZE.M}
        justifyContent="space-between">
        <Stack
          direction="row"
          alignItems="center"
          spacing={SIZE.M}
          fullWidth={false}>
          <Button
            data-test-id="back-button"
            aria-label={t('customer.backToCustomerList')}
            icon="arrowBack"
            variant="outlined"
            onClick={() => navigate('/customers')}
          />
          <Stack>
            <Text variant="titleStandard" color="PrimaryMain">
              {customer?.name}
            </Text>
            <Stack
              direction="row"
              alignItems="center"
              spacing={SIZE.S}
              flexWrap>
              <Badge
                label={t('customer.customerIdBadge', {
                  id: customer?.customerId,
                })}
                variant="displayMonsoon"
              />
              <Badge
                label={t('customer.domainIdBadge', { id: customerId })}
                variant="displayMonsoon"
              />
              <Badge
                label={t('customer.mainUnitIdBadge', {
                  id: customer?.rootStoreId,
                })}
                variant="displayMonsoon"
              />
              {isMarkedForDeletion && (
                <Badge
                  label={t('customer.markedForDeletionBadge', {
                    date: customer?.deleteBy,
                  })}
                  variant="notice"
                />
              )}
            </Stack>
          </Stack>
        </Stack>

        <Button
          data-test-id="view-customer-manager-portal-button"
          text={t('customer.viewManagerPortal')}
          leftIcon="qLogo"
          intent="alternate"
          onClick={() => {
            window.open(
              `${CONFIG.MANAGER_PORTAL}/dashboard/${customerId}`,
              '_blank',
            );
          }}
        />
      </Stack>

      <Tabs
        key={activeTabId}
        defaultSelected={activeTabIndex}
        fullWidth
        aria-label={t('customer.customerInformationTabs')}>
        <Tabs.Button
          key={TAB_IDS.BASIC_INFO}
          data-test-id="basic-information-tab-button"
          text={t('customer.basicInformation')}
          onClick={() => handleTabChange(TAB_IDS.BASIC_INFO)}
        />
        <Tabs.Button
          key={TAB_IDS.LICENSE}
          data-test-id="license-tab-button"
          text={t('customer.licenses')}
          onClick={() => handleTabChange(TAB_IDS.LICENSE)}
        />
        <Tabs.Button
          key={TAB_IDS.MODULES}
          data-test-id="modules-tab-button"
          text={t('customer.modules')}
          onClick={() => handleTabChange(TAB_IDS.MODULES)}
        />
        <Tabs.Button
          key={TAB_IDS.CONFIGS}
          data-test-id="configs-tab-button"
          text={t('customer.configs')}
          onClick={() => handleTabChange(TAB_IDS.CONFIGS)}
        />
        <Tabs.Button
          key={TAB_IDS.ZOINED}
          data-test-id="zoined-tab-button"
          text={t('customer.zoined')}
          onClick={() => handleTabChange(TAB_IDS.ZOINED)}
        />
        <Tabs.Button
          key={TAB_IDS.ACTIONS}
          data-test-id="action-tab-button"
          text={t('customer.actions')}
          onClick={() => handleTabChange(TAB_IDS.ACTIONS)}
        />
      </Tabs>

      <Stack
        spacing={SIZE.M}
        borderRadius={SIZE.L}
        p={SIZE.M}
        elevation={ELEVATION.SMOOTH}
        backgroundColor="SurfaceMain">
        {activeTabId === TAB_IDS.BASIC_INFO && (
          <CustomerForm
            isLoading={isLoading}
            onSubmit={handleSubmit}
            initialValues={customer}
            error={updateError}
            isMarkedForDeletion={isMarkedForDeletion}
          />
        )}
        {activeTabId === TAB_IDS.LICENSE && (
          <LicenseForm
            domainId={String(customerId)}
            isDisabled={isMarkedForDeletion}
          />
        )}
        {activeTabId === TAB_IDS.MODULES && (
          <Modules
            customerId={String(customerId)}
            isDisabled={isMarkedForDeletion}
          />
        )}
        {activeTabId === TAB_IDS.CONFIGS && (
          <Config
            customerId={String(customerId)}
            isDisabled={isMarkedForDeletion}
          />
        )}
        {activeTabId === TAB_IDS.ZOINED && (
          <ZoinedForm
            initialValues={initialZoined}
            onSubmit={handleSubmitZoined}
            isDisabled={isMarkedForDeletion}
          />
        )}
        {activeTabId === TAB_IDS.ACTIONS && (
          <Actions
            customerId={String(customerId)}
            customer={customer}
            isMarkedForDeletion={isMarkedForDeletion}
          />
        )}
      </Stack>
    </Stack>
  );
};

export default withCustomerLayout(Customer);
